/**
 * Shared option lists for the Personal Data Sheet.
 *
 * KEEP IN SYNC with frontend/src/components/pdsFormat.js — the advisor SPA
 * is a separate CRA app with no shared package, so these lists exist in two
 * places. If they drift, the exported PDF will disagree with what the client
 * actually saw on screen.
 */

/**
 * Schwab's required occupation list, in the order it appears in their account
 * application. Value === label deliberately, so the stored string can be
 * pasted straight into the application with no lookup table.
 *
 * One deliberate departure from Schwab's list: their "Other professional" is
 * dropped, because sitting next to "Other (please specify)" it just made
 * clients guess which "other" was meant.
 */
export const OCCUPATIONS = [
  'Business owner/Self-employed',
  'Executive/Senior management',
  'Medical professional',
  'Legal professional',
  'Accounting professional',
  'Financial services/Banking professional',
  'Information technology professional',
  "US Gov't Employee",
  "Foreign gov't employee (non-US)",
  'Military',
  'Educator',
  'Clerical/Administrative services',
  'Trade/Service',
  'Sales/Marketing',
  'Consultant',
  'Other (please specify)',
];

export const OCCUPATION_OTHER = 'Other (please specify)';

export const EMPLOYMENT_STATUSES = [
  { value: 'employed', label: 'Employed' },
  { value: 'self_employed', label: 'Self-employed' },
  { value: 'not_employed', label: 'Not employed' },
  { value: 'retired', label: 'Retired' },
  { value: 'homemaker', label: 'Homemaker' },
  { value: 'student', label: 'Student' },
];

/** Occupation is only collected for these statuses (per the source doc). */
export const OCCUPATION_STATUSES = ['employed', 'self_employed'];

/** Beneficiary relationships, verbatim from the custodian's dropdown. */
export const BENEFICIARY_RELATIONSHIPS = [
  'Spouse',
  'Child',
  'Grandchild',
  'Parent',
  'Sibling',
  'Other Individual',
  'Trust',
  'Organization',
  'Estate',
];

/** Relationship of a Trusted Contact Person to the client. */
export const TCP_RELATIONSHIPS = [
  'Spouse',
  'Child',
  'Grandchild',
  'Parent',
  'Sibling',
  'Other Relative',
  'Friend',
  'Attorney',
  'Accountant',
  'Other',
];

/**
 * Security questions used to verify a caller. Stored as the question text
 * itself rather than a slug — it goes onto the PDF and into the CRM, and a
 * slug would need a lookup table in three more places.
 */
export const SECURITY_QUESTIONS = [
  'What was the name of your first pet?',
  'In what city were you born?',
  'What was the make and model of your first car?',
  'What is the name of the street you grew up on?',
  'What was the name of your elementary school?',
];

/**
 * How often the client wants to hear from us beyond their own account emails
 * and the quarterly reports. Each option is cumulative on the one above it,
 * which is why the labels read "Option A + ...".
 */
export const COMMUNICATION_FREQUENCIES = [
  {
    value: 'a',
    label: 'Option A: Quarterly newsletters, event invitations, and curated client articles',
  },
  { value: 'b', label: 'Option B: Option A + extra educational articles' },
  { value: 'c', label: 'Option C: Option B + anything else you feel is relevant to me' },
];

export const MAX_BENEFICIARIES = 8;
